// One-line phrasing of a contract's extension options — "2 × 1-year extension
// options". Shared by the contract term progress bar and the report summary's
// contract-essentials card.
//
// Its own leaf module (rather than an export from TermProgressBar) so a test
// that mocks the progress-bar component does not have to reproduce this pure
// helper in the mock factory.
//
// Takes the options object rather than a contract, because the report wire
// model carries `extensionOptions` without the surrounding contract record.

import type { ExtensionOptions } from "../../types";

/** Returns undefined when there is nothing to say — no options, or options
 *  with no periods — so callers can omit the line entirely rather than
 *  rendering an empty phrase. */
export function extensionPeriodSummary(
  opts: ExtensionOptions | null | undefined,
): string | undefined {
  if (!opts || opts.periods.length === 0) return undefined;
  const years = opts.durationUnit === "years" ? opts.durationValue : 0;
  const months = opts.durationUnit === "months" ? opts.durationValue : 0;
  const days = opts.durationUnit === "days" ? opts.durationValue : 0;
  const optionWord = opts.periods.length === 1 ? "option" : "options";
  if (days > 0) {
    // Note the day branch pluralises the duration word ("30-days") where the
    // year/month branches below hyphenate a singular compound modifier
    // ("30-month"). Preserved verbatim from the original inline helper — this
    // module was extracted without behaviour change.
    const dayWord = days === 1 ? "day" : "days";
    return `${opts.periods.length} × ${days}-${dayWord} extension ${optionWord}`;
  }
  // Hyphenate the duration when it acts as a compound modifier:
  // "1-year option" / "2-year option" — singular noun before the attribute.
  // A free-standing "1 year" / "2 years" reads awkwardly in this position.
  //
  // `durationUnit` names ONE unit and `durationValue` is validated at least 1,
  // so exactly one of years/months/days is positive here and the days case has
  // already returned. The original inline helper carried a third
  // mixed-years-and-months branch left over from an earlier two-field model;
  // it was unreachable under the current schema and is not reproduced.
  const durationModifier =
    years > 0 ? `${years}-year` : `${months}-month`;
  return `${opts.periods.length} × ${durationModifier} extension ${optionWord}`;
}
